import React, { useRef, useState } from 'react';
import {
  Platform,
  StyleProp,
  StyleSheet,
  TextInput,
  TextInputProps,
  TextStyle,
  View,
} from 'react-native';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import {
  PressableAnalyticsProps,
  usePressableAnalytics,
} from 'shared/hooks/analytics/usePressableAnalytics';
import theme from 'shared/styles/theme';
import { VaroFontNames } from 'shared/styles/typography';

export enum NoteInputVariant {
  SHORT = 'short',
  LONG = 'long',
}
interface NoteInputProps extends TextInputProps, PressableAnalyticsProps {
  /**
   * The placeholder text for Input field.
   */
  placeholderText: string;
  /**
   * Primary label text.
   */
  primaryText?: string;
  /**
   * Used to disable the Input field.
   */
  isDisabled?: boolean;

  /**
   * Gap in between label and input field.
   */
  gap?: number;

  /**
   * Variant of the input field,
   * if variant is SHORT, the height of the input field will be reduced and vice versa.
   *
   */
  variant?: NoteInputVariant;
  /**
   * TextInput background color.
   */
  backgroundColor?: string;
}

const getTextInputStyle = (
  isFocused: boolean,
  variant: NoteInputVariant,
  styles: { [key: string]: StyleProp<TextStyle> },
  isDisabled?: boolean,
) => {
  const styleArray = [styles.textInput];

  if (isFocused && !isDisabled) {
    styleArray.push(styles.focusedInput);
  }

  if (variant === NoteInputVariant.SHORT) {
    styleArray.push(styles.shortHeightVariant);
  }

  return styleArray;
};

const NoteInput = ({
  placeholderText,
  primaryText,
  onChangeText,
  isDisabled,
  value,
  analyticsId = '',
  customAttributes = null,
  gap = theme.spacing.m,
  variant = NoteInputVariant.LONG,
  backgroundColor,
  maxLength,
  onFocus,
  onBlur,
  testID,
}: NoteInputProps) => {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<TextInput>(null);

  const textFieldAnalytics = usePressableAnalytics({
    analyticsId,
    customAttributes: { ...customAttributes },
  });
  const handleChange = (text: string) => {
    onChangeText?.(text);
  };

  return (
    <View style={[styles.container, { gap: gap }]}>
      {primaryText ? (
        <BodySmBoldText
          numberOfLines={0}
          style={isDisabled && styles.disabledTitle}
        >
          {primaryText}
        </BodySmBoldText>
      ) : null}

      <View style={styles.inputField}>
        <TextInput
          editable={!isDisabled}
          ref={inputRef}
          testID={testID}
          style={[
            getTextInputStyle(isFocused, variant, styles, isDisabled),
            backgroundColor && { backgroundColor: backgroundColor },
          ]}
          onChangeText={handleChange}
          placeholderTextColor={theme.color.text.tertiary}
          value={value}
          maxLength={maxLength}
          placeholder={placeholderText}
          selectionColor={theme.color.text.primary}
          onFocus={(event) => {
            setIsFocused(true);
            textFieldAnalytics();
            if (onFocus) {
              onFocus(event);
            }
          }}
          onBlur={(event) => {
            setIsFocused(false);
            if (onBlur) {
              onBlur(event);
            }
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    backgroundColor: theme.color.interactive.secondary,
    flexDirection: 'row',
  },
  disabledTitle: {
    color: theme.color.interactive.disabled.text,
  },
  focusedInput: {
    borderBottomColor: theme.color.interactive.primary,
  },
  inputField: {
    flex: 1,
    width: '100%',
  },
  shortHeightVariant: {
    borderBottomColor: 'transparent',
    borderBottomWidth: 0,
    height: 23,
    paddingVertical: 0,
  },
  textInput: {
    alignItems: 'center',
    borderBottomColor: theme.color.border.primarySolid,
    borderBottomWidth: 1,
    borderWidth: 0,
    color: theme.color.text.primary,
    fontFamily: VaroFontNames.NeueHaasGroteskDisplayProRomanNormal,
    fontSize: 16,
    height: 48,
    letterSpacing: 0.02,
    lineHeight: 22,
    ...Platform.select({
      web: {
        outlineStyle: 'solid',
        outlineWidth: 0,
      },
    }),
  },
});

export default NoteInput;
